import { DownloadButton } from "../../components/common/DownloadButton";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { WellInputs } from "./WellInputs";
import { useCallback, useMemo } from "react";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import { getDataForVLP } from "../../store/slices/pvtCalculatorSlice";
import { CalculateButton } from "../../components/common/CalculateButton";
import { InputRow } from "./InputRow";
import type { IDDItem } from "../../components/common/DropdownMenu";
import { unitConverter } from "../../components/MeasurementUnits/UnitConverter";
import "./VLP.css";
import { useTranslation } from "react-i18next";
import { setInputValue, updateInputByUnit, validateInputs } from "../../store/slices/vlpCalculatorSlice";
import type { IDisplayField, IField } from "../../interfaces/interfaces";
import { OuterData } from "../../components/common/OuterData";
import { getSecondWellIsOpen } from "../../store/slices/appSlice";


const validated = (inputs: IField[] | IDisplayField[]) => {
	return !inputs.some(input => input.error);
}

export const InputSection = () => {
	const inputs = useAppSelector(state => state.vlpCalculator.inputs);
	const well1 = useAppSelector(state => state.vlpCalculator.well1);
	const well2 = useAppSelector(state => state.vlpCalculator.well2);
	const secondWell = useAppSelector(getSecondWellIsOpen);

	const [triggerCalculator] = useCalculatorMutation();
	const pvtData = useAppSelector(getDataForVLP);
	const dispatch = useAppDispatch();

	const onCalculate = useCallback(async () => {
		dispatch(validateInputs());

		const formData1 = getFormData("well1");
		const formData2 = getFormData("well2");

		if (!formData1)
			return;

		if (secondWell && !formData2)
			return;

		triggerCalculator({
			calculator_type: "vlp",
			well: "1",
			vlp_criteria: well1.criteria.selected,
			...formData1,
		});

		if (secondWell)
			triggerCalculator({
				calculator_type: "vlp",
				well: "2",
				vlp_criteria: well2.criteria.selected,
				...formData2,
			});
	}, [secondWell, triggerCalculator, well1, well2]);

	const getFormData = useCallback((wellId: "well1" | "well2") => {
		dispatch(validateInputs());

		const well = wellId === "well1" ? well1 : well2;
		const vlpInputs = [...inputs, ...well.inputs, ...pvtData.inputs];
		if (!well || !validated(vlpInputs) || pvtData.errors.length > 0) {
			return;
		}

		const formData: Record<string, number | string> = {};

		vlpInputs.forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});

		formData['well'] = wellId === "well1" ? "1" : "2";

		return formData;
	}, [inputs, well1, well2, pvtData, secondWell]);

	const onInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(
				setInputValue({ inputName: inputName, value: value })
			);
		},
		[dispatch, well1, well2, inputs]
	);

	const onSelectUnit = useCallback((inputId: string, selectedUnitId: string) => {
		dispatch(
			updateInputByUnit({
				inputId: inputId,
				selectedUnitId: selectedUnitId,
			})
		);
	}, [dispatch, well1, well2, inputs]);

	const unitsDDItems: { [key: string]: IDDItem[] } = useMemo(() => {
		return inputs.reduce<Record<string, IDDItem[]>>((acc, input) => {
			const units = unitConverter.getUnitsByCategory(input.unitCategoryId);
			acc[input.id] = units.map(unit => ({
				id: unit.id,
				translationKey: `measurement_units.${unit.category}.${unit.id}`
			} as IDDItem));
			return acc;
		}, {});

	}, [inputs]);

	const { t } = useTranslation("vlp");

	return <div className="vlp__input-section">
		<div className="vlp__common-section">
			<fieldset className="vlp__inputs">
				{inputs.map((input) => (
					<InputRow
						key={`vlp__${input.id}`}
						inputId={input.id}
						dropdownItems={unitsDDItems[input.id]}
						onInputChange={onInputChange}
						onSelectUnit={onSelectUnit}
					/>
				))}
			</fieldset>
		</div>
		<div className="vlp__wells-section">
			{<WellInputs wellId="well1" />}
			{secondWell && <WellInputs wellId="well2" />}
			<div className="vlp__buttons">
				<CalculateButton onClick={onCalculate} />
				<DownloadButton getFormData={() => getFormData("well1")} wellId="well1" calculatorType="vlp" disabled={false} title={t(`download`)} />
				{secondWell && <DownloadButton getFormData={() => getFormData("well2")} wellId="well2" calculatorType="vlp" disabled={false} title={t(`download`)} />}
			</div>
			<OuterData
				inputs={pvtData.inputs}
				errors={pvtData.errors}
				translationNamespace="pvt"
			/>
		</div>
	</div >;
}

